/**
 * DieticianRegistrationPage Component
 *
 * Main page component for the dietitian registration workflow.
 * Integrates with the DieticianRegistrationWizard and existing Layout components.
 * Provides responsive design and SEO optimization.
 *
 * @module components/pages/DieticianRegistrationPage
 */

import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import { Layout } from '@/components/layout/Layout';
import { DieticianRegistrationWizard } from '@/components/dietician-registration/DieticianRegistrationWizard';
import { useTheme } from '@/hooks/useTheme';
import { BodyText, TitleText } from '../ui';

/**
 * DieticianRegistrationPage component props interface
 */
export interface DieticianRegistrationPageProps {
  /** Custom class name */
  className?: string;
  /** Callback when registration completes */
  onRegistrationComplete?: (registrationId: string) => void;
  /** Callback when user wants to contact support */
  onContactSupport?: () => void;
  /** Callback when user wants to go to dashboard */
  onGoToDashboard?: () => void;
}

/**
 * Step-specific content for SEO and accessibility
 */
const STEP_CONTENT = {
  1: {
    title: 'Create Your Dietitian Account',
    description: 'Join our network of Accredited Practicing Dietitians and start helping patients achieve their nutrition goals'
  },
  2: {
    title: 'Complete Your Professional Profile',
    description: 'Add your credentials, specializations, and practice details'
  },
  3: {
    title: 'Set Your Availability',
    description: 'Configure your schedule and consultation preferences'
  },
  4: {
    title: 'Registration Complete',
    description: 'Welcome to BiteWise! Your dietitian registration is complete'
  }
};

/**
 * DieticianRegistrationPage component for the complete registration workflow
 *
 * @example
 * ```tsx
 * <DieticianRegistrationPage
 *   onRegistrationComplete={(registrationId) => {
 *     analytics.track('dietician_registration_complete', { registrationId });
 *     navigate('/dashboard');
 *   }}
 * />
 * ```
 */
export const DieticianRegistrationPage: React.FC<DieticianRegistrationPageProps> = ({
  className,
  onRegistrationComplete
}) => {
  const { isDark } = useTheme();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isCompleted, setIsCompleted] = useState(false);

  /**
   * Handle step changes for analytics and SEO
   */
  const handleStepChange = (step: number) => {
    setCurrentStep(step);

    // Update page title for better UX
    const stepContent = STEP_CONTENT[step as keyof typeof STEP_CONTENT];
    if (stepContent && typeof document !== 'undefined') {
      document.title = `${stepContent.title} - BiteWise`;
    }
  };

  /**
   * Handle registration completion
   */
  const handleRegistrationComplete = (registrationId: string) => {
    setIsCompleted(true);
    onRegistrationComplete?.(registrationId);
  };

  /**
   * Get current step content for accessibility
   */
  const getCurrentStepContent = () => {
    return STEP_CONTENT[currentStep as keyof typeof STEP_CONTENT] || STEP_CONTENT[1];
  };

  const stepContent = getCurrentStepContent();

  return (
    <>
      <Layout className={cn('min-h-screen', className)}>
        {/* Page Header */}
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-3xl mx-auto text-center">
              <TitleText>
                {stepContent.title}
              </TitleText>
              <BodyText
                variant="secondary"
                className="text-lg"
              >
                {stepContent.description}
              </BodyText>

              {/* Progress Indicator for Screen Readers */}
              <div className="sr-only" aria-live="polite" aria-atomic="true">
                Currently on step: {currentStep} of 4
              </div>
            </div>
          </div>

        {/* Main Content */}
        <main className={cn(
          "flex-1",
          isDark ? "bg-secondary-900" : "bg-white"
        )}>
          <div className="container mx-auto px-4 py-4 sm:py-6">
            {/* Registration Wizard */}
            <DieticianRegistrationWizard
              onComplete={handleRegistrationComplete}
              onStepChange={handleStepChange}
              className="max-w-4xl mx-auto"
            />

            {/* Additional Information */}
            {!isCompleted && (
              <div className="max-w-2xl mx-auto mt-4">
                <div className={cn(
                  "rounded-lg p-6",
                  isDark ? "bg-secondary-900" : "bg-secondary-50"
                )}>
                  <h2 className={cn(
                    "text-lg font-semibold mb-3",
                    isDark ? "text-secondary-100" : "text-secondary-900"
                  )}>
                    Why Join BiteWise as an Accredited Practicing Dietitian?
                  </h2>
                  <ul className={cn(
                    "space-y-2 text-sm",
                    isDark ? "text-secondary-400" : "text-secondary-600"
                  )}>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Receive qualified patient referrals from healthcare providers
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Flexible scheduling with digital consultation management
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Expand your practice with verified healthcare partnerships
                    </li>
                    <li className="flex items-start">
                      <svg className={cn(
                        "w-4 h-4 mt-0.5 mr-2 flex-shrink-0",
                        isDark ? "text-success-400" : "text-success-600"
                      )} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      Competitive compensation with transparent fee structure
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </main>
      </Layout>
    </>
  );
};

export default DieticianRegistrationPage;